import { captureMessage } from '@sentry/nextjs';
import { GraphQLError } from './errors';
import {
  request as graphqlRequest,
  rawRequest as rawGraphqlRequest,
  Variables as DefaultVariables,
  RequestOptions,
} from 'graphql-request';

export type { DefaultVariables };
import axios from 'axios';

const defaultUrl =
  process.env.NODE_ENV === 'test'
    ? `${process.env.NEXT_PUBLIC_WEB_ENV}/api/graphql`
    : (process.env.NEXT_PUBLIC_GRAPHQL_API_URL ?? '/api/graphql');

const appendHeader = (
  headers: Headers | string[][] | Record<string, string> | undefined,
  key: string,
  value: string,
) => {
  const headersObj =
    headers instanceof Headers ? headers : new Headers(Object.entries(headers ?? {}));
  headersObj.set(key, value);
  return headersObj;
};

export type GraphQLRequestProps<Variables extends DefaultVariables = DefaultVariables> =
  RequestOptions<Variables> & {
    url?: string;
    locale: string;
  };

export type GraphQLRequestWithTokenProps<Variables extends DefaultVariables = DefaultVariables> =
  GraphQLRequestProps<Variables> & {
    idToken: string | null;
  };

export const rawRequest = async <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>({
  url = defaultUrl,
  locale,
  ...rest
}: GraphQLRequestProps<Variables>) => {
  if (!locale) {
    return;
  }

  return rawGraphqlRequest<Data, Variables>({
    ...rest,
    url,
    requestHeaders: appendHeader(rest.requestHeaders, 'Accept-Language', locale),
  } as any);
};

export const rawRequestWithToken = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>({
  requestHeaders,
  idToken,
  ...rest
}: GraphQLRequestWithTokenProps<Variables>) => {
  return rawRequest<Data, Variables>({
    requestHeaders: idToken
      ? appendHeader(requestHeaders, 'Authorization', idToken)
      : requestHeaders,
    ...(rest as unknown as GraphQLRequestProps<Variables>),
  });
};

export const rawRequestWithTokenRequired = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>(
  props: GraphQLRequestWithTokenProps<Variables>,
) => {
  if (!props.idToken) {
    return;
  }
  return rawRequestWithToken<Data, Variables>(props);
};

export const request = <Data = any, Variables extends DefaultVariables = DefaultVariables>({
  url = defaultUrl,
  locale,
  ...rest
}: GraphQLRequestProps<Variables>) => {
  if (!locale) {
    return;
  }

  return graphqlRequest<Data, Variables>({
    ...rest,
    url,
    requestHeaders: {
      'Accept-Language': locale,
      ...rest.requestHeaders,
    },
  } as any);
};

export const requestWithToken = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>({
  requestHeaders,
  idToken,
  ...rest
}: GraphQLRequestWithTokenProps<Variables>) =>
  request<Data, Variables>({
    requestHeaders: idToken ? { ...requestHeaders, Authorization: idToken } : requestHeaders,
    ...(rest as unknown as GraphQLRequestProps<Variables>),
  });

export const requestWithTokenRequired = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>(
  props: GraphQLRequestWithTokenProps<Variables>,
) => {
  if (!props.idToken) {
    return;
  }
  return requestWithToken<Data, Variables>(props);
};

export const requestServer = async <Data = any>(
  props: Omit<GraphQLRequestProps, 'locale' | 'idToken'>,
) => {
  const { data } = await axios.post(
    defaultUrl as string,
    { query: props.document, variables: props.variables },
    {
      headers: props.requestHeaders as any,
    },
  );

  if (data.errors) {
    if (!data.data) {
      const { message, code } = data?.errors?.[0] ?? {
        message: 'No message provided',
        code: 500,
      };

      throw new GraphQLError(message, code);
    }

    captureMessage('uncaught graphql error', {
      level: 'error',
      extra: {
        document: props.document,
        variables: props.variables,
        errorMessage: data.errors,
      },
    });
  }

  return data.data as Data;
};
